import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import { orderListPage } from "@/lib/db/list-order";
import type { SharedAccount } from "@/lib/types/finance";
import { cast } from "@/lib/utils/query-helpers";
import { ACCOUNT_SELECT } from "./selects";

/**
 * Columns of `gl_accounts` a list may order by.
 *
 * Declared beside the query because sorting is server-side: the union confines
 * what can reach `.order()`, and the accounts feature derives its URL-facing
 * sort allowlist from this constant so the two cannot drift.
 */
export const ACCOUNT_SORT_COLUMNS = [
  "balance",
  "created_at",
  "customer_balance",
  "name",
  "type",
  "uid",
  "vendor_balance",
] as const;

export type AccountSortColumn = (typeof ACCOUNT_SORT_COLUMNS)[number];

export interface AccountListParams {
  readonly limit?: number;
  readonly offset?: number;
  readonly search?: string;
  readonly sort_direction?: "asc" | "desc";
  /**
   * Already confined to {@link AccountSortColumn} by the type: a URL-supplied
   * string must pass the feature's `isAccountSortField` guard to get here.
   */
  readonly sort_field?: AccountSortColumn;
  readonly type?: "customer" | "vendor";
}

interface AccountPage {
  limit: number;
  offset: number;
}

function getAccountPage(params?: AccountListParams): AccountPage {
  return {
    limit: Math.max(1, params?.limit ?? 50),
    offset: Math.max(0, params?.offset ?? 0),
  };
}

/** Alphabetical by name, the order this list has always opened in. */
function resolveAccountSort(params?: AccountListParams): {
  ascending: boolean;
  column: AccountSortColumn;
} {
  if (params?.sort_field) {
    return {
      ascending: params.sort_direction !== "desc",
      column: params.sort_field,
    };
  }
  return { ascending: true, column: "name" };
}

/**
 * The filters an account list narrows by, applied in one place.
 *
 * They land after the order and the page window, so the narrowing never
 * reorders the sequence the `.range()` slice was cut from. Neither branch
 * widens what RLS already allows — `is_customer` and `is_vendor` are ordinary
 * columns on rows the policy has already admitted.
 */
function applyAccountListFilters<T>(
  query: QueryBuilder<T>,
  params: AccountListParams = {},
): QueryBuilder<T> {
  let next = query;

  switch (params.type) {
    case "customer":
      next = next.eq("is_customer", true);
      break;
    case "vendor":
      next = next.eq("is_vendor", true);
      break;
    default:
      break;
  }

  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) {
    next = next.ilike("name", `%${trimmedSearch}%`);
  }

  return next;
}

/** How many accounts a search matches, split by the role each one plays. */
export interface AccountSummaryCounts {
  both: number;
  customers: number;
  total: number;
  vendors: number;
}

/** One `head: true` count over the accounts a search matches. */
function countAccountsMatching(search: string | undefined) {
  const backend = getBackendClient();

  return applyAccountListFilters(
    backend.data.from("gl_accounts").select("id", { count: "exact", head: true }),
    { search },
  );
}

/**
 * The account totals a list summary reports, counted server-side.
 *
 * Counted rather than derived from the rows in view: a list holds one page, so
 * a total taken from it reports the page size as the size of the book and
 * changes every time someone steps forward.
 *
 * The scope narrowing is deliberately left off. These three role figures *are*
 * the type breakdown, so applying the chip would zero the two roles the
 * operator did not pick and leave the third repeating the total.
 */
export async function getAccountSummaryCounts(
  params?: Pick<AccountListParams, "search">,
): Promise<AccountSummaryCounts> {
  const { search } = params ?? {};

  const [total, customers, vendors, both] = await Promise.all([
    countAccountsMatching(search),
    countAccountsMatching(search).eq("is_customer", true),
    countAccountsMatching(search).eq("is_vendor", true),
    countAccountsMatching(search).eq("is_customer", true).eq("is_vendor", true),
  ]);

  const failed = [total, customers, vendors, both].find((result) => result.error);
  if (failed?.error) {
    throw new Error(failed.error.message);
  }

  return {
    both: both.count ?? 0,
    customers: customers.count ?? 0,
    total: total.count ?? 0,
    vendors: vendors.count ?? 0,
  };
}

export async function getAccounts<TAccount extends SharedAccount = SharedAccount>(
  params?: AccountListParams,
): Promise<TAccount[]> {
  const backend = getBackendClient();
  const { limit, offset } = getAccountPage(params);

  // `orderListPage` appends the unique tiebreaker, so a page window is a
  // genuine slice of one sequence even when many accounts share a name.
  let query = orderListPage(
    backend.data.from("gl_accounts").select(ACCOUNT_SELECT),
    resolveAccountSort(params),
  ).range(offset, offset + limit - 1);

  query = applyAccountListFilters(query, params);

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }

  return cast<TAccount[]>(data || []);
}
